// Fields.js
// Builds option caches via FieldOptions first, then produces renderable field configs.
// Unified RenderRequest pipeline; external signature compatible with ViewFields.

import * as FieldRenderers from "./FieldRenderers.js";
import { RenderContext } from "../Enums.js";
import Typography from "@mui/material/Typography";
import FieldOptions from "./FieldOptions.js";
import AuthorsPanel from "../components/DataGrid/DetailPanel/AuthorsPanel.js";
import {
  getAiSuggestedDisplayValue,
  getAiSuggestedMeta,
} from "./AiFieldHelpers.js";

/**
 * Utility: safe nested getter
 */
export const getNestedPropertyValue = (object, path) => {
  if (!object || typeof object !== "object" || !path || typeof path !== "string") return undefined;
  return path
    .split(".")
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), object);
};

/**
 * Utility: unified field key reader
 * Prefer `.field`; if absent, use `.key`. Returns "" when neither is present.
 * Used only for external resolver calls that may supply { key } instead of { field }.
 */
const getFieldKey = (obj) => {
  if (!obj || typeof obj !== "object") return "";
  return obj.field != null ? String(obj.field)
       : obj.key   != null ? String(obj.key)
       : "";
};

// Forward reference: calls resolveOptionsForField, defined further down this file.
// Works fine (function declarations hoist), just reads out of dependency order.
const resolveAiSuggestedTargetOptions = (field) => {
  const targetFieldKey =
    field?.suggestsField ||
    field?.targetFieldKey ||
    field?.details?.suggestedFieldKey ||
    field?.details?.targetFieldKey ||
    field?.details?.fieldKey ||
    field?.attributes?.suggestedFieldKey ||
    "";

  if (!targetFieldKey) {
    return { flatOptions: [], treeOptions: [] };
  }

  return resolveOptionsForField({ field: targetFieldKey });
};


/**
 * Resolve flat/tree options for a field.
 * Contract:
 *   - Always attempt asset-backed options via FieldOptions (internally using AssetRegistry).
 *   - If not found, fall back to inline `field.options` when present.
 *   - No dictionaries; no optionsSource/optionsAssetKey.
 *
 * NOTE: Accepts either { field, ... } or { key, ... } from external callers.
 */
export function resolveOptionsForField(fieldConfig) {
  // Boolean-backed renderers have a fixed true/false value space. Skip the
  // asset-by-field-name / inline lookup entirely for them: an unrelated
  // AssetRegistry entry that happens to share the field's name would
  // otherwise silently replace {true,false} and hide a valid value (e.g.
  // a stored `true` failing renderSelect's options-membership check).
  if (fieldConfig?.renderType === "BooleanSelect" || fieldConfig?.renderType === "AIAssignedBadge") {
    return {
      flatOptions: [
        { name: "Yes", value: true },
        { name: "No", value: false },
      ],
      treeOptions: [],
    };
  }

  const fieldName = getFieldKey(fieldConfig);
  const inline = fieldConfig?.options;

  const treeOptions = FieldOptions.resolveTreeOptions({ fieldName, options: inline });
  const flatOptions = FieldOptions.resolveFlatOptions({ fieldName, options: inline });

  return { flatOptions, treeOptions };
}

/**
 * Internal request object passed to renderers:
 * {
 *   field, row, value,
 *   env: { context, theme, projectCode, locale, projectConfig },
 *   cb:  { onCellUpdate, onFileUpdate, onReviewerChange, onOpenAiReviewPanel },
 *   options: { flatOptions, treeOptions }
 * }
 */
export const makeRenderRequest = ({
  field,
  row,
  value,
  context,
  theme,
  projectCode,
  projectConfig,
  locale,
  onCellUpdate,
  onFileUpdate,
  onReviewerChange,
  onOpenAiReviewPanel,
}) => {
  // Centralized editability policy:
  // - META_PANEL: always read-only
  // - Other contexts: keep field.editable as-is
  const effectiveField = {
    ...field,
    editable: context === RenderContext.META_PANEL ? false : !!field?.editable,
  };

  return {
    field: effectiveField,
    row,
    value,
    env: { context, theme, projectCode, locale, projectConfig },
    cb: { onCellUpdate, onFileUpdate, onReviewerChange, onOpenAiReviewPanel },
    options: resolveOptionsForField(effectiveField),
  };
};

/**
 * Unified renderField(req): dispatcher by fieldType
 * - All select-like fields use fieldType: "select" and flow through FieldRenderers.renderSelect(req)
 *   (country/language/boolean/pill/tree are picked by field.renderType template)
 */
export const renderField = (req) => {
  const { field, value, row } = req;
  const { fieldType } = field;

  const onCell = (next) => req.cb.onCellUpdate?.(row.id, next, field.dataSource);

  const renderers = {
    // === Unified select ===
    select: () => FieldRenderers.renderSelect(req),

    // === Non-selects retained ===
    "Author&Affiliations": () => {
      const affiliations = getNestedPropertyValue(row, field.affiliationsDataSource);
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderAuthors(value, affiliations);
        case RenderContext.META_PANEL:
          return FieldRenderers.renderAuthorsForDetailPanel(value, affiliations);
        case RenderContext.DETAIL_PANEL:
          return (
            <AuthorsPanel
              authors={Array.isArray(value) ? value : []}
              affiliations={Array.isArray(affiliations) ? affiliations : []}
              readOnly={!field.editable}
              onSave={async (nextAuthors, nextAffiliations) => {
                const updates = [{ field: field.dataSource, value: nextAuthors }];
                if (field.affiliationsDataSource) {
                  updates.push({ field: field.affiliationsDataSource, value: nextAffiliations });
                }
                await req.cb.onCellUpdate?.(row.id, updates);
              }}
            />
          );
        default:
          return null;
      }
    },

    asset_file: () => {
      //console.log("Rendering asset_file field", { row, field });
      switch (req.env.context) {
        case RenderContext.DATAGRID:
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderAssetFileDownloadButton({
            linkedData: row.linkedData,
            rowId: row.id,
            field,
            theme: req.env.theme,
            context: req.env.context,
          });

        default:
          return null;
      }
    },

    authors: () => {
      const affiliations = getNestedPropertyValue(row, field.affiliationsDataSource);
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderAuthors(value);
        case RenderContext.META_PANEL:
          return FieldRenderers.renderAuthorsForDetailPanel(value, affiliations);
        case RenderContext.DETAIL_PANEL:
          return (
            <AuthorsPanel
              authors={Array.isArray(value) ? value : []}
              affiliations={Array.isArray(affiliations) ? affiliations : []}
              readOnly={!field.editable}
              onSave={async (nextAuthors, nextAffiliations) => {
                const updates = [{ field: field.dataSource, value: nextAuthors }];
                if (field.affiliationsDataSource) {
                  updates.push({ field: field.affiliationsDataSource, value: nextAffiliations });
                }
                await req.cb.onCellUpdate?.(row.id, updates);
              }}
            />
          );
        default:
          return null;
      }
    },

    affiliations: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderAffiliations(value);
        default:
          return null;
      }
    },

    aiScreening: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderAiReviewCellPanel({
            value,
            fieldConfig: field,
            row,
            additionalProps: {
              onCellUpdate: req.cb.onCellUpdate,
              onFileUpdate: req.cb.onFileUpdate,
              onReviewerChange: req.cb.onReviewerChange,
              onOpenAiReviewPanel: req.cb.onOpenAiReviewPanel,
              projectCode: req.env.projectCode,
              locale: req.env.locale,
            },
            theme: req.env.theme,
          });
        default:
          return null;
      }
    },

    aiPresentation: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID: {
          if (!value || typeof value !== "object") return null;

          // `strengthKey` names a property on the AI result object (`value`) to read.
          // `field.details.strength.dataSource` lets a view override which property;
          // "combined" is the default property name when no override is configured.
          const strengthKey = field?.details?.strength?.dataSource || "combined";
          const strength = value?.[strengthKey];

          const displayValue =
            strength === null || strength === undefined ? "" : String(strength);

          return FieldRenderers.renderEllipsisCell({ displayValue });
        }
        // META_PANEL/DETAIL_PANEL not implemented yet — not a deliberate grid-only design.
        default:
          return null;
      }
    },

    aiStructure: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID: {
          if (!value || typeof value !== "object") return null;

          // Same override mechanism as aiPresentation above: `scoreKey`/`alignmentKey`
          // name properties on the AI result object (`value`), overridable per-view via
          // field.details.*.dataSource; "structureScore"/"titleAlignment" are the defaults.
          const scoreKey = field?.details?.score?.dataSource || "structureScore";
          const alignmentKey = field?.details?.alignment?.dataSource || "titleAlignment";

          const structureScore = value?.[scoreKey];
          const titleAlignment = value?.[alignmentKey];

          const parts = [];

          if (typeof structureScore === "number") {
            parts.push(`Structure ${Math.round(structureScore * 100)}%`);
          }

          if (typeof titleAlignment === "number") {
            parts.push(`Title ${Math.round(titleAlignment * 100)}%`);
          }

          const displayValue = parts.join(" • ");

          return FieldRenderers.renderEllipsisCell({ displayValue });
        }
        // META_PANEL/DETAIL_PANEL not implemented yet — not a deliberate grid-only design.
        default:
          return null;
      }
    },

    aiTheme: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID: {
          if (!value || typeof value !== "object") return null;

          return FieldRenderers.renderEllipsisCell({
            displayValue: "AI theme",
            title: "AI theme data available",
          });
        }
        // META_PANEL/DETAIL_PANEL not implemented yet — not a deliberate grid-only design.
        default:
          return null;
      }
    },

    aiSuggestedField: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID: {
          const targetOptions = resolveAiSuggestedTargetOptions(field);
          const { displayValue, isAmbiguous, candidateCount } = getAiSuggestedMeta(value, field, targetOptions);
          const tone = isAmbiguous ? req.env.theme.aiAmbiguousColour : req.env.theme.aiColour;

          return FieldRenderers.renderAiSuggestedBadge({ displayValue, isAmbiguous, candidateCount, tone });
        }
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderReadonlyText(
            getAiSuggestedDisplayValue(value, field, resolveAiSuggestedTargetOptions(field)),
            req.env.context
          );
        default:
          return null;
      }
    },


    dateTime: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderDateTimeCell({
            onChange: (e) => onCell(e.target.value),
            editable: field.editable,
            value,
            context: req.env.context,
          });
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderDateTimeCellForDetailPanel({
            onChange: (e) => onCell(e.target.value),
            editable: field.editable,
            value,
            context: req.env.context,
          });
        default:
          return null;
      }
    },

    DMYdate: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderDateCell({
            onChange: (e) => onCell(e.target.value),
            editable: field.editable,
            value,
            context: req.env.context,
          });
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderDateCellForDetailPanel({
            onChange: (e) => onCell(e.target.value),
            editable: field.editable,
            value,
            context: req.env.context,
          });
        default:
          return null;
      }
    },

    file: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return FieldRenderers.renderFileCell(row, field.field, req.env.projectCode, req.env.theme);
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.downloadFileForDetailPanel({
            row,
            dataKey: field.field,
            projectCode: req.env.projectCode,
            onFileUpdate: req.cb.onFileUpdate,
          });
        default:
          return null;
      }
    },

    int: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return value;
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return <Typography variant="body1">{value}</Typography>;
        default:
          return null;
      }
    },

    multiline: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return value;
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderMultiLineTextField({
            value,
            onBlur: (content) => req.cb.onCellUpdate?.(row.id, content, field.dataSource),
            attributes: field.attributes ? field.attributes : null,
          });
        default:
          return null;
      }
    },

    notifications: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          // JSX (not a plain function call) — NotificationBell holds its own open/
          // close state for the click-to-view feed, which needs a real component.
          return (
            <FieldRenderers.NotificationBell
              value={value}
              persistedNotifications={row.persistedNotifications}
              fieldLabelsByKey={row.fieldLabelsByKey}
              theme={req.env.theme}
              onDismiss={row.onDismissNotifications}
              trackingNumber={row.meta?.trackingNumber}
              entityTitle={row.entityTitle}
            />
          );
        // META_PANEL/DETAIL_PANEL intentionally not implemented yet.
        default:
          return null;
      }
    },

    owner: () => FieldRenderers.getDataFromOwner(value, field.field),

    progress: () => {
      switch (req.env.context) {
        case RenderContext.META_PANEL: {
          const displayValue = value != null ? `${value}%` : "";
          return FieldRenderers.renderReadonlyText(displayValue, req.env.context);
        }
        case RenderContext.DATAGRID:
        case RenderContext.DETAIL_PANEL:
          return FieldRenderers.renderProgressBar(value, req.env.theme);
        default:
          return null;
      }
    },

    // Generic per-role badge (submitter/reviewer/admin/…) — which role a column
    // represents comes from field.attributes.userRole, not field.field/dataSource,
    // so dataSource keeps doing its normal job (value + native column sort).
    userRole: () =>
      FieldRenderers.renderUserRole({
        value,
        field,
        theme: req.env.theme,
        editable: field.editable,
        onChange: (e) =>
          FieldRenderers.handleReviewerChange(row.id, e.target.value, req.cb.onReviewerChange, field.field),
      }),

    reviewer_specialist_areas: () => FieldRenderers.renderReviewerSpecialistAreas(req),

    reviewingType: () =>
      FieldRenderers.renderReviewingType({
        onChange: (e) =>
          FieldRenderers.handleSelectChange(
            row.id,
            e.target.value,
            field.dataSource,
            req.cb.onCellUpdate
          ),
        editable: field.editable,
        value,
        options: req.options.flatOptions,
        context: req.env.context,
      }),

    secondaryOwner: () => FieldRenderers.getDataFromOwner(value, field.field),

    string: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID: {
          const displayValue =
            value === null || value === undefined ? "" : String(value);

          return (
            <span className="rt-grid-ellipsis" title={displayValue}>
              {displayValue}
            </span>
          );
        }
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          if (field.editable) {
            return FieldRenderers.renderTextInputControl({
              value,
              editable: true,
              onBlur: (e) => {
                const currentValue = value;
                const newValue = e.target.value;
                if (currentValue !== newValue) {
                  req.cb.onCellUpdate?.(row.id, newValue, field.dataSource);
                }
              },
              onKeyDown: (e) => {
                if (e.key === "Enter") e.target.blur();
              },
            });
          }
          return FieldRenderers.renderReadonlyText(value, req.env.context);
        default:
          return null;
      }
    },

    total: () => {
      switch (req.env.context) {
        case RenderContext.DATAGRID:
          return value;
        case RenderContext.META_PANEL:
        case RenderContext.DETAIL_PANEL:
          return <Typography variant="body1">{value}</Typography>;
        default:
          return null;
      }
    },

    trackingNumber: () => FieldRenderers.renderReadonlyText(value, req.env.context),
  };

  // Fallback for unmapped fieldType — usually a stale view config (pre-v4.2.0
  // rename, no migration run). Console-only, not an email — known recurring issue.
  const renderer =
    renderers[fieldType] ||
    (() => {
      const message =
        `Fields.js: unrecognized fieldType "${fieldType}" for field "${field?.field}" ` +
        `(dataSource: ${field?.dataSource}, headerName: "${field?.headerName}"). ` +
        `Project code: ${req.env.projectCode ?? "unknown"}. Row id: ${row?.id ?? "unknown"}.`;
      console.warn(message, field);
      return req.env.context === RenderContext.DATAGRID
        ? value
        : <Typography variant="body1">{value}</Typography>;
    });

  return renderer();
};

/**
 * Build fields (async)
 * External callers (ViewFields) rely on this default export signature.
 *
 * Signature UPDATED: dictionaries removed; options resolved via FieldOptions/AssetRegistry.
 */
async function Fields(
  fieldDefinitions,
  projectCode,
  getOnlyVisible,
  filterType,
  onCellUpdate,
  onFileUpdate,
  onReviewerChange,
  onOpenAiReviewPanel,
  theme,
  locale,
  projectConfig
) {
  FieldOptions.init();

  await FieldOptions.buildFromFields({
    fields: fieldDefinitions,
  });

  const processedFields = fieldDefinitions
    .filter((field) => !filterType || field.fieldType === filterType)
    .filter((field) => !getOnlyVisible || field.visible)
    .map((field) => {
      const newField = {
        ...field,
        valueGetter: (params) => getNestedPropertyValue(params.row, field.dataSource),
        renderCell: (params) => {
          const req = makeRenderRequest({
            field,
            row: params.row,
            value: getNestedPropertyValue(params.row, field.dataSource),
            context: RenderContext.DATAGRID,
            theme,
            projectCode,
            projectConfig,
            locale,
            onCellUpdate,
            onFileUpdate,
            onReviewerChange,
            onOpenAiReviewPanel,
          });
          return renderField(req);
        },
        renderHeader: FieldRenderers.makeHeaderRenderer(field, theme),
      };

      if (field.field === "trackingNumber") {
        newField.sortComparator = (v1, v2) => {
          const val1 = v1 == null ? Number.MAX_SAFE_INTEGER : v1;
          const val2 = v2 == null ? Number.MAX_SAFE_INTEGER : v2;
          return val1 - val2;
        };
      }

      if (field.fieldType === "file" || field.fieldType === "asset_file") {
        newField.align = "center";
        newField.headerAlign = "center";
      }

      return newField;
    });

  return processedFields;
}

export default Fields;
